import { DefaultErrorMessages } from "@helpers/formValidationHelper";
import { Organization } from "@helpers/organizationHelper";
import { jobTitleOptions } from "@helpers/selectHelper";
import { yupResolver } from "@hookform/resolvers/yup";
import { useModal } from "@services/ModalContext";
import { useApi } from "@services/useApi";
import { useState, ChangeEvent } from "react";
import { FormProvider, useForm } from "react-hook-form";
import { object, string } from "yup";
import { LBtn } from "./LBtn";
import { LDivider } from "./LDivider";
import { LInputField } from "./LInputField";
import { LModal, LModalWidth } from "./LModal";
import { LSelect, SelectOption } from "./LSelect";

export interface EditPrimaryContactForm {
    email: string;
    firstName: string;
    lastName: string;
    phoneNumber?: string;
    jobTitle?: string;
    otherJobTitle?: string;
}

const formSchema = object({
    email: string().email("Invalid email").optional(),
    firstName: string().required(DefaultErrorMessages.required),
    lastName: string().required(DefaultErrorMessages.required),
    phoneNumber: string()
        .matches(/^\d+$/, DefaultErrorMessages.numeric("Phone number"))
        .length(10, DefaultErrorMessages.lengthDigits(10, "Phone number"))
        .optional(),
    jobTitle: string().required(DefaultErrorMessages.required),
    otherJobTitle: string().when("jobTitle", (val, schema) => {
        return val === "Other" ? schema.required("Other job title is required") : schema;
    })
});

const EditPrimaryContactModalHeader = (): JSX.Element => {
    return <div className="has-text-weight-bold">Edit Primary Contact</div>;
};

export const EditPrimaryContactModal = ({
    fullHeight = false,
    onClose,
    onSubmit,
    organization,
    width = "s"
}: EditPrimaryContactModalProps): JSX.Element => {
    const { closeModal } = useModal();

    const { updateOrganizationPrimaryContact, loading } = useApi();

    const isOtherJobTitle =
        !!organization?.primaryContact?.jobTitle &&
        !jobTitleOptions.map(({ name }) => name).includes(organization?.primaryContact?.jobTitle);

    const [showOther, setShowOther] = useState(isOtherJobTitle);

    const validateMethods = useForm<EditPrimaryContactForm>({
        resolver: yupResolver(formSchema)
    });

    const formSubmitHandler = async (primaryContact: EditPrimaryContactForm): Promise<void> => {
        try {
            const { jobTitle, otherJobTitle } = primaryContact;
            const payload = {
                ...primaryContact,
                jobTitle: jobTitle === "Other" ? otherJobTitle : jobTitle
            } as EditPrimaryContactForm;

            await updateOrganizationPrimaryContact(organization.id!, payload);

            onSubmit?.(payload);

            onClose();

            closeModal();
        } catch (err) {
            alert(err);
        }
    };

    const handleUpdate = async (e: React.MouseEvent): Promise<void> => {
        await validateMethods.handleSubmit(formSubmitHandler)(e);
    };

    const handleSelect = (e: ChangeEvent<HTMLInputElement>): void => {
        setShowOther(e.target.value === "Other");
    };

    const handleClose = (): void => {
        onClose();

        closeModal();
    };

    return (
        <LModal title={<EditPrimaryContactModalHeader />} onClose={onClose} fullHeight={fullHeight} width={width}>
            <FormProvider {...validateMethods}>
                <form onSubmit={validateMethods.handleSubmit(formSubmitHandler)}>
                    <LInputField
                        label="Primary contact first name"
                        inputName="firstName"
                        defaultValue={organization?.primaryContact?.firstName}
                    />
                    <LInputField
                        label="Primary contact last name"
                        inputName="lastName"
                        defaultValue={organization?.primaryContact?.lastName}
                    />
                    <LInputField
                        label="Primary contact email"
                        inputName="email"
                        defaultValue={organization?.primaryContact?.email}
                    />
                    <LInputField
                        label="Primary contact phone number"
                        inputName="phoneNumber"
                        defaultValue={organization?.primaryContact?.phoneNumber}
                    />
                    <LSelect
                        options={jobTitleOptions}
                        label="Job title"
                        defaultValue={isOtherJobTitle ? "Other" : organization?.primaryContact?.jobTitle}
                        defaultOption={{ name: "Select job title", value: "" } as SelectOption}
                        onChange={handleSelect}
                        inputName="jobTitle"
                    />
                    {showOther && (
                        <LInputField
                            inputName="otherJobTitle"
                            label="Please specify other job title"
                            placeholder="e.g. Clinical Psychologist"
                            defaultValue={isOtherJobTitle ? organization?.primaryContact?.jobTitle : ""}
                        />
                    )}
                    <LDivider />
                    <div className="columns px-3 is-justify-content-space-between is-align-items-center">
                        <div className="column is-two-fifths">
                            <LBtn variation="outline" fullWidth onClick={handleClose}>
                                Cancel
                            </LBtn>
                        </div>
                        <div className="column is-two-fifths">
                            <LBtn fullWidth loading={loading} onClick={handleUpdate}>
                                Update
                            </LBtn>
                        </div>
                    </div>
                </form>
            </FormProvider>
        </LModal>
    );
};

interface EditPrimaryContactModalProps {
    fullHeight?: boolean;
    onClose: () => void;
    onSubmit?: (() => void) | ((updatedPrimaryContact: EditPrimaryContactForm) => void);
    organization: Organization;
    width?: LModalWidth;
}
